/**
 * What a series-wide edit actually changes.
 *
 * The edit modal shows every field of the night it was opened on, so sending
 * the whole form would stamp that night's title, description and tags onto
 * every night in the series — including a theme night whose description is
 * the point of it. Sending only what the editor changed keeps each field
 * independent: moving the series to Thursdays touches the date and nothing
 * else.
 */

/** The fields a series-wide edit can set, as the update mutation takes them. */
export interface SeriesFields {
  title: string;
  description: string | undefined;
  startTime: string;
  endTime: string | undefined;
  lessonStartTime: string | undefined;
  lessonEndTime: string | undefined;
  venueName: string;
  venueAddress: string | undefined;
  venueId: string | undefined;
  venueLatitude: number | undefined;
  venueLongitude: number | undefined;
  danceStyles: string[];
  mainStyles?: string[];
  tags: string[] | undefined;
  eventUrl: string | undefined;
  priceMin: number | undefined;
  priceMax: number | undefined;
  priceUnit: string | undefined;
}

/**
 * Fields that only mean something together. A venue is its name, address and
 * place; a price is its amounts and unit. Changing one sends the group, so a
 * night never ends up with one venue's name and another's address.
 */
const GROUPS: { label: string; compare: (keyof SeriesFields)[]; send: (keyof SeriesFields)[] }[] = [
  { label: "Title", compare: ["title"], send: ["title"] },
  { label: "Description", compare: ["description"], send: ["description"] },
  { label: "Start time", compare: ["startTime"], send: ["startTime"] },
  { label: "End time", compare: ["endTime"], send: ["endTime"] },
  {
    label: "Lesson",
    compare: ["lessonStartTime", "lessonEndTime"],
    send: ["lessonStartTime", "lessonEndTime"],
  },
  {
    label: "Venue",
    // Coordinates follow the address; a re-geocode alone is not an edit.
    compare: ["venueName", "venueAddress", "venueId"],
    send: ["venueName", "venueAddress", "venueId", "venueLatitude", "venueLongitude"],
  },
  // Main styles are a subset of the styles, so they travel together.
  { label: "Dance styles", compare: ["danceStyles", "mainStyles"], send: ["danceStyles", "mainStyles"] },
  { label: "Tags", compare: ["tags"], send: ["tags"] },
  { label: "Link", compare: ["eventUrl"], send: ["eventUrl"] },
  {
    label: "Price",
    compare: ["priceMin", "priceMax", "priceUnit"],
    send: ["priceMin", "priceMax", "priceUnit"],
  },
];

/** Empty text and empty lists read the same as a field left unset. */
function normalize(value: unknown): string {
  if (value === undefined || value === null || value === "") return "";
  if (Array.isArray(value) && value.length === 0) return "";
  return JSON.stringify(value);
}

/**
 * The fields of `next` that differ from `original`, and a label for each
 * change, for telling the editor what is about to be applied.
 */
export function changedSeriesFields(
  original: SeriesFields,
  next: SeriesFields
): { fields: Partial<SeriesFields>; labels: string[] } {
  const fields: Partial<SeriesFields> = {};
  const labels: string[] = [];
  for (const group of GROUPS) {
    if (group.compare.every((k) => normalize(original[k]) === normalize(next[k]))) continue;
    labels.push(group.label);
    for (const k of group.send) (fields as Record<string, unknown>)[k] = next[k];
  }
  return { fields, labels };
}
